<template>
  <div>
    <section-collapseable
      title="Address"
      editable
      :collapseable="!!hasLocation"
      @edit="$refs.propertyModal.open(property)"
      @toggled="loadStreetViewURL"
    >
      <template
        v-if="
          property.line_1 ||
          property.city ||
          property.state ||
          property.postcode
        "
      >
        <sensitive-data-wrapper>
          <p v-if="property.line_1" class="property_address">
            {{ property.line_1 }}
          </p>
          <p v-if="property.line_2" class="property_address">
            {{ property.line_2 }}
          </p>
          <p
            v-if="property.city || property.state || property.postcode"
            class="property_address"
          >
            {{ property.city }}, {{ property.state }} {{ property.postcode }}
          </p>
        </sensitive-data-wrapper>
      </template>

      <p v-else class="property_address">Unknown Address</p>

      <a
        v-if="hasLocation"
        class="zillow_link"
        :href="zillowLinkURL"
        target="_blank"
        >View on Zillow
      </a>

      <a
        v-if="hasLocation"
        class="google_map"
        :href="mapLinkURL"
        target="_blank"
      >
        <img :src="mapImageURL" alt="Google Map" />
        <span>View In Google Maps</span>
      </a>

      <!-- Expanded Section -->
      <template v-slot:expanded>
        <div v-if="showStreetView" class="has-text-centered">
          <img :src="streetViewURL" alt="Street View Image" />
        </div>
        <div v-else class="empty-street-view">
          <b-loading :is-full-page="false" active></b-loading>
        </div>
      </template>
    </section-collapseable>

    <modal-container>
      <modal-crud-form
        ref="propertyModal"
        title="Property"
        form-component="property-address-form"
        :hideDeleteButton="true"
      ></modal-crud-form>
    </modal-container>
  </div>
</template>

<script>
import SensitiveDataWrapper from "../../../components/SensitiveDataWrapper.vue";

export default {
  name: "LeadAddress",
  components: { SensitiveDataWrapper },
  data() {
    return {
      showStreetView: false,
    };
  },
  computed: {
    property() {
      // If activeRecord is null then return necessary empty info
      return this.$store.state.activeRecord
        ? this.$store.state.activeRecord.property
        : {
            line_1: null,
            city: null,
            state: null,
            postcode: null,
          };
    },
    hasLocation() {
      return !!this.property.line_1;
    },
    streetViewURL() {
      if (!this.hasLocation) return;

      let url = "https://maps.googleapis.com/maps/api/streetview";

      url += "?size=300x180";
      url += "&location=" + this.locationString("+");
      url += "&key=" + process.env.MIX_GOOGLE_PLACES_BROWSER_API_KEY;

      return url;
    },
    mapImageURL() {
      if (!this.hasLocation) return;

      let url = "https://maps.googleapis.com/maps/api/staticmap";

      url += "?center=" + this.locationString("+");
      url += "&zoom=12";
      url += "&size=640x320";
      url += "&scale=2";
      url += "&markers=color:red" + "|" + this.locationString("+");
      url += "&format=jpg";
      url += "&key=" + process.env.MIX_GOOGLE_PLACES_BROWSER_API_KEY;

      return url;
    },
    mapLinkURL() {
      if (!this.hasLocation) return;

      return "https://www.google.com/maps/place/" + this.locationString("+");
    },
    zillowLinkURL() {
      if (!this.hasLocation) return;

      return "https://www.zillow.com/homes/" + this.locationString("-");
    },
  },
  methods: {
    locationString(deliminator = "+") {
      if (!this.property.line_1) {
        return;
      }

      const address = this.property.line_1 || "";
      const city = this.property.city || "";
      const state = this.property.state || "";
      const postcode = this.property.postcode || "";

      const full = `${address} ${city} ${state} ${postcode}`;

      return full
        .replace(/#\s*/g, "") // remove # or "# "
        .replace(/,/g, "") // remove commas
        .replace(/\s+/g, deliminator) // spaces to deliminator
        .replace(/-+/g, deliminator) // collapse multiple deliminator
        .trim();
    },
    loadStreetViewURL(value) {
      this.showStreetView = value;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../../styles/variables/variables";

.property_address {
  font-size: 1.125rem;
  line-height: 1.25;
}

.google_map {
  display: block;
  margin-top: 10px;

  &:hover {
    img {
      outline-offset: 2px;
      outline: 1px solid $primary;
    }
  }

  span {
    margin-top: 4px;
    font-size: 0.75rem;
  }
}

.zillow_link {
  display: block;
  margin: 2px 0 16px;
  font-size: 0.75rem;
}

.empty-street-view {
  min-height: 180px;
  width: 100%;
}
</style>
